/**
 * User-facing release notes, shown by the "What's new" menu in the header.
 *
 * Deliberately NOT generated from CHANGELOG.md: that file is written for
 * developers and auditors (it carries S-SDLC control references, infra and
 * supply-chain entries) and is mostly in French only. This list is the
 * short, bilingual, user-facing subset — a release that changes nothing a
 * user can see simply gets no entry here.
 *
 * Maintenance: add a new object at the TOP of NEWS when you tag a release.
 * `version` must match the git tag without the leading "v", because the
 * unread badge compares it against the last version the user has seen.
 */
export interface NewsItem {
  fr: string;
  en: string;
}

export interface NewsEntry {
  /** Semver, no leading "v" — must match the git tag. */
  version: string;
  /** ISO date, displayed localised. */
  date: string;
  items: NewsItem[];
}

/** Newest first — the list is rendered in this order and NEWS[0] drives the badge. */
export const NEWS: NewsEntry[] = [
  {
    version: "1.2.1",
    date: "2026-10-05",
    items: [
      {
        fr: "Correction : si vous posez deux demi-journées sur la même date (une matinée de CP et une après-midi de RTT, par exemple), le calendrier affiche désormais la journée entière en congé. Elle n'apparaissait qu'à moitié, et l'infobulle ne mentionnait qu'un seul des deux types.",
        en: "Fixed: booking two half-days on the same date (a morning of paid leave and an afternoon of RTT, say) now shows as a full day off in the calendar. It used to show as half a day, and the tooltip named only one of the two leave types.",
      },
    ],
  },
  {
    version: "1.2.0",
    date: "2026-09-04",
    items: [
      {
        fr: "Survolez une date en haut du calendrier : l'infobulle liste maintenant les personnes absentes ce jour-là, avec le type de congé, au lieu d'afficher seulement la date.",
        en: "Hover a date at the top of the calendar: the tooltip now lists who is away that day, with the leave type, instead of just showing the date.",
      },
      {
        fr: "Ce menu « Nouveautés » — pour savoir ce qui change à chaque version sans avoir à demander.",
        en: "This \"What's new\" menu — so you can see what changed in each version without having to ask.",
      },
    ],
  },
  {
    version: "1.1.1",
    date: "2026-07-06",
    items: [
      {
        fr: "Session expirée : l'application vous ramène proprement à l'écran de connexion au lieu d'afficher une erreur. Un clic suffit pour revenir, sans ressaisir votre mot de passe.",
        en: "Expired session: the app now returns you cleanly to the sign-in screen instead of showing an error. One click brings you back, with no password to retype.",
      },
      {
        fr: "L'aperçu du mode « Discret » dans les préférences montre le vrai rendu vu par vos collègues.",
        en: "The \"Private\" preview in Settings now shows what your colleagues actually see.",
      },
    ],
  },
  {
    version: "1.1.0",
    date: "2026-07-06",
    items: [
      {
        fr: "Nouveau mode « Discret » : votre nom reste visible mais aucune de vos absences n'apparaît. Les collègues voient que vous existez, pas votre planning.",
        en: "New \"Private\" mode: your name stays visible but none of your absences show. Colleagues see that you exist, not your schedule.",
      },
      {
        fr: "Case « Masquer les discrets » pour alléger la vue du calendrier.",
        en: "\"Hide private\" checkbox to declutter the calendar view.",
      },
      {
        fr: "Managers : case « Voir comme un employé » pour vérifier ce que votre équipe voit réellement.",
        en: "Managers: \"View as employee\" checkbox to check what your team actually sees.",
      },
      {
        fr: "Vos filtres et votre tri sont conservés d'une visite à l'autre.",
        en: "Your filters and sort order are kept between visits.",
      },
    ],
  },
  {
    version: "1.0.0",
    date: "2026-06-25",
    items: [
      {
        fr: "Première version : calendrier des absences alimenté par Boondmanager, connexion par votre compte Gatewatcher, et réglages de visibilité par personne.",
        en: "First release: absence calendar fed from Boondmanager, sign-in with your Gatewatcher account, and per-person visibility settings.",
      },
    ],
  },
];

/** Version of the most recent entry — what the unread badge compares against. */
export const LATEST_NEWS_VERSION: string = NEWS[0]?.version ?? "";
